import { parsePhoneWithExtension } from '../utils/validation';
import {
	checkFormSignature,
	FORM_SIGNATURE_MAX_LENGTH,
} from './form-signature';
import type {
	FormAnswerValue,
	FormDefinitionModel,
	FormFieldDefinitionModel,
	FormFieldErrorCode,
	FormFieldType,
	FormRuleErrorModel,
	FormRuleModel,
	FormValidationResultModel,
} from './form-types';

/**
 * The one validator for Workd Forms (docs/claude/forms/01-design.md §3d).
 *
 * crm-api runs it on every submission and is the authority; the storefront and the CRM
 * run the same function as the visitor types, so the page and the server can never
 * disagree about what is valid. Pure and dependency-free apart from other libs/shared
 * helpers, so it runs unchanged in a browser and in Node.
 *
 * Nothing is silently truncated: an answer longer than its limit is refused with
 * `too_long`, so what is stored is always exactly what the visitor typed (after trimming
 * and removing control characters and markup).
 */

/**
 * Hard ceilings per type, applied when the admin set no smaller `maxLength`. They keep
 * one submission from carrying megabytes, and they bound the input any admin `pattern`
 * is run against.
 */
export const FORM_FIELD_DEFAULT_MAX_LENGTH: Readonly<
	Record<FormFieldType, number>
> = {
	text: 500,
	textarea: 5000,
	number: 40,
	currency: 40,
	date: 10,
	datetime: 40,
	boolean: 10,
	select: 255,
	multiselect: 255,
	phone: 40,
	email: 254,
	url: 2048,
	consent: 10,
	hidden: 500,
	signature: FORM_SIGNATURE_MAX_LENGTH,
	teamMember: 20,
};

/** An admin pattern longer than this is refused when the form is saved. */
export const FORM_PATTERN_MAX_LENGTH = 200;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATETIME_PATTERN =
	/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?$/;
// Control characters other than tab and newline (which a textarea legitimately holds).
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const LINE_BREAKS_AND_TABS = /[\t\r\n]+/g;
const MARKUP = /<[^>]*>/g;

/** The result of checking one field: nothing answered, a clean value, or an error. */
export type FormFieldOutcome =
	| { kind: 'empty' }
	| { kind: 'value'; value: FormAnswerValue }
	| { kind: 'error'; code: FormFieldErrorCode };

/**
 * Validate and clean a set of answers against a form definition.
 *
 * - Only keys of fields in the definition are read; anything else is dropped, and keys are
 *   read as own properties only, so `constructor` or `toString` cannot leak in.
 * - Empty optional answers are left out of `answers`.
 * - A rule error is reported only when none of the rule's fields was answered at all, so
 *   an invalid email is reported once, against the email field, not twice.
 */
export function validateFormAnswers(
	definition: FormDefinitionModel,
	rawAnswers: unknown,
): FormValidationResultModel {
	const raw: Record<string, unknown> =
		rawAnswers != null &&
		typeof rawAnswers === 'object' &&
		!Array.isArray(rawAnswers)
			? (rawAnswers as Record<string, unknown>)
			: {};

	const answers: Record<string, FormAnswerValue> = {};
	const errors: Record<string, FormFieldErrorCode> = {};
	const answeredKeys = new Set<string>();

	for (const field of definition.fields) {
		const input = Object.prototype.hasOwnProperty.call(raw, field.fieldKey)
			? raw[field.fieldKey]
			: undefined;

		const outcome = validateFieldValue(field, input);

		if (outcome.kind === 'empty') {
			if (field.isRequired) {
				errors[field.fieldKey] = 'required';
			}
			continue;
		}

		answeredKeys.add(field.fieldKey);

		if (outcome.kind === 'error') {
			errors[field.fieldKey] = outcome.code;
			continue;
		}

		answers[field.fieldKey] = outcome.value;
	}

	const ruleErrors = evaluateFormRules(
		definition.fields,
		definition.rules,
		answeredKeys,
	);

	return {
		valid: Object.keys(errors).length === 0 && ruleErrors.length === 0,
		answers,
		errors,
		ruleErrors,
	};
}

/**
 * Validate one field's value. Exported for the renderer, which checks a field as the
 * visitor leaves it.
 */
export function validateFieldValue(
	field: FormFieldDefinitionModel,
	input: unknown,
): FormFieldOutcome {
	switch (field.fieldType) {
		case 'boolean':
			return validateBoolean(input, false);
		case 'consent':
			return validateBoolean(input, true);
		case 'number':
		case 'currency':
			return validateNumber(field, input);
		case 'select':
			return validateSelect(field, input);
		case 'teamMember':
			// PD-16691 — one of the people listed, as a user id. The page and the server both
			// check it against the choices worked out for the form right now, which they put
			// in `selectOptions` (the stored definition has none).
			return validateSelect(field, input);
		case 'multiselect':
			return validateMultiselect(field, input);
		case 'date':
			return validateDate(field, input);
		case 'datetime':
			return validateDatetime(field, input);
		case 'email':
			return validateText(field, input, (value) =>
				EMAIL_PATTERN.test(value) ? null : 'invalid_email',
			);
		case 'url':
			return validateText(field, input, (value) =>
				isHttpUrl(value) ? null : 'invalid_url',
			);
		case 'phone':
			return validatePhone(field, input);
		case 'signature':
			return validateSignature(input);
		case 'text':
		case 'textarea':
		case 'hidden':
			return validateText(field, input, () => null);
		default:
			// A type the validator does not know (a newer definition read by an older page)
			// is treated as plain text rather than rejected outright.
			return validateText(field, input, () => null);
	}
}

/** The fields a rule names that are actually on the form. */
function ruleFieldKeys(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	rule: FormRuleModel,
): Array<string> {
	const onForm = new Set(fields.map((field) => field.fieldKey));
	return rule.fieldKeys.filter((key) => onForm.has(key));
}

function evaluateFormRules(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	rules: ReadonlyArray<FormRuleModel>,
	answeredKeys: ReadonlySet<string>,
): Array<FormRuleErrorModel> {
	const ruleErrors: Array<FormRuleErrorModel> = [];

	// "At least one of" is the only rule type, and a definition with any other is refused
	// when the form is saved (checkFormDefinition), so every stored rule is one of these.
	for (const rule of rules) {
		const keys = ruleFieldKeys(fields, rule);
		// A rule whose fields have all been removed from the form constrains nothing.
		if (keys.length > 0 && !keys.some((key) => answeredKeys.has(key))) {
			ruleErrors.push({
				rule: 'at_least_one',
				fieldKeys: keys,
				message: atLeastOneMessage(fields, rule, keys),
			});
		}
	}

	return ruleErrors;
}

/**
 * "Enter Email or Phone" wording built from the fields' public labels, since the visitor
 * reads it. A rule's own message wins when the admin wrote one.
 */
export function atLeastOneMessage(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	rule: FormRuleModel,
	keys: ReadonlyArray<string>,
): string {
	if (rule.message != null && rule.message.trim().length > 0) {
		return rule.message.trim();
	}

	const labels = keys.map((key) => {
		const field = fields.find((candidate) => candidate.fieldKey === key);
		return field?.label ?? key;
	});

	if (labels.length === 1) {
		return `Enter ${labels[0]}`;
	}

	const last = labels[labels.length - 1];
	return `Enter ${labels.slice(0, -1).join(', ')} or ${last}`;
}

/**
 * Plain text only: trim, drop control characters and markup. Single-line types also fold
 * line breaks and tabs into spaces, so a header or a spreadsheet cell cannot be split.
 */
export function cleanFormText(value: string, multiline: boolean): string {
	let cleaned = value.replace(CONTROL_CHARACTERS, '').replace(MARKUP, '');
	if (!multiline) {
		cleaned = cleaned.replace(LINE_BREAKS_AND_TABS, ' ');
	}
	return cleaned.trim();
}

function toText(input: unknown): string | null {
	if (typeof input === 'string') {
		return input;
	}
	if (typeof input === 'number' && Number.isFinite(input)) {
		return String(input);
	}
	return null;
}

function maxLengthFor(field: FormFieldDefinitionModel): number {
	// Read as possibly missing: a definition can name a type this code does not know yet.
	const ceilings: Readonly<Record<string, number | undefined>> =
		FORM_FIELD_DEFAULT_MAX_LENGTH;
	const ceiling = ceilings[field.fieldType] ?? 500;
	const adminMax = field.validationRules?.maxLength;
	return adminMax != null && adminMax < ceiling ? adminMax : ceiling;
}

/**
 * Run an admin pattern against the whole value. The pattern is anchored so "5 digits"
 * cannot pass "abc12345xyz", and it only ever sees values already capped by the field's
 * maximum length (a pattern longer than FORM_PATTERN_MAX_LENGTH is refused at save),
 * which bounds how long a badly written pattern can run. A pattern that does not compile
 * is ignored here; the definition check refuses it when the form is saved.
 */
function matchesPattern(pattern: string, value: string): boolean {
	let expression: RegExp;
	try {
		expression = new RegExp(`^(?:${pattern})$`);
	} catch {
		return true;
	}
	return expression.test(value);
}

function validateText(
	field: FormFieldDefinitionModel,
	input: unknown,
	checkFormat: (value: string) => FormFieldErrorCode | null,
): FormFieldOutcome {
	if (input == null) {
		return { kind: 'empty' };
	}

	const text = toText(input);
	if (text == null) {
		return { kind: 'error', code: 'invalid_type' };
	}

	const value = cleanFormText(text, field.fieldType === 'textarea');
	if (value.length === 0) {
		return { kind: 'empty' };
	}

	if (value.length > maxLengthFor(field)) {
		return { kind: 'error', code: 'too_long' };
	}

	const minLength = field.validationRules?.minLength;
	if (minLength != null && value.length < minLength) {
		return { kind: 'error', code: 'too_short' };
	}

	const formatError = checkFormat(value);
	if (formatError != null) {
		return { kind: 'error', code: formatError };
	}

	const pattern = field.validationRules?.pattern;
	if (
		pattern != null &&
		pattern.length > 0 &&
		!matchesPattern(pattern, value)
	) {
		return { kind: 'error', code: 'invalid_format' };
	}

	return { kind: 'value', value };
}

function validatePhone(
	field: FormFieldDefinitionModel,
	input: unknown,
): FormFieldOutcome {
	const outcome = validateText(field, input, () => null);
	if (outcome.kind !== 'value' || typeof outcome.value !== 'string') {
		return outcome;
	}

	// Stored in E.164 so matching compares like with like; an extension is kept after it.
	const { e164, extension } = parsePhoneWithExtension(outcome.value);
	if (e164 == null) {
		return { kind: 'error', code: 'invalid_phone' };
	}

	return {
		kind: 'value',
		value: extension != null ? `${e164} ext ${extension}` : e164,
	};
}

function isHttpUrl(value: string): boolean {
	try {
		const url = new URL(value);
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

function validateBoolean(input: unknown, isConsent: boolean): FormFieldOutcome {
	if (input == null || input === '') {
		return { kind: 'empty' };
	}

	let value: boolean | null = null;
	if (typeof input === 'boolean') {
		value = input;
	} else if (
		input === 1 ||
		input === '1' ||
		input === 'true' ||
		input === 'on'
	) {
		value = true;
	} else if (input === 0 || input === '0' || input === 'false') {
		value = false;
	}

	if (value == null) {
		return { kind: 'error', code: 'invalid_type' };
	}

	// An unticked consent box is no consent: for a required consent that reads as missing.
	if (isConsent && !value) {
		return { kind: 'empty' };
	}

	return { kind: 'value', value };
}

/**
 * PD-16691 — a drawn signature, as the PNG data URL the pad produces. Checked as sent and
 * never cleaned: trimming or stripping characters would silently change the image. A
 * required signature that was not drawn reads as missing.
 */
function validateSignature(input: unknown): FormFieldOutcome {
	if (input == null || input === '') {
		return { kind: 'empty' };
	}
	if (typeof input !== 'string') {
		return { kind: 'error', code: 'invalid_type' };
	}
	const check = checkFormSignature(input);
	return check === 'ok'
		? { kind: 'value', value: input }
		: { kind: 'error', code: check };
}

function validateNumber(
	field: FormFieldDefinitionModel,
	input: unknown,
): FormFieldOutcome {
	if (input == null || input === '') {
		return { kind: 'empty' };
	}

	let value: number;
	if (typeof input === 'number') {
		value = input;
	} else if (typeof input === 'string') {
		const cleaned = input.trim();
		if (cleaned.length === 0) {
			return { kind: 'empty' };
		}
		if (cleaned.length > maxLengthFor(field)) {
			return { kind: 'error', code: 'too_long' };
		}
		// Thousands separators, and a leading dollar sign on currency, are how people type
		// amounts; anything else that is not a plain decimal number is refused.
		const normalized = cleaned.replace(/,/g, '').replace(/^\$/, '');
		if (!/^-?(\d+\.?\d*|\.\d+)$/.test(normalized)) {
			return { kind: 'error', code: 'invalid_number' };
		}
		value = Number(normalized);
	} else {
		return { kind: 'error', code: 'invalid_type' };
	}

	if (!Number.isFinite(value)) {
		return { kind: 'error', code: 'invalid_number' };
	}

	const min = field.validationRules?.min;
	if (typeof min === 'number' && value < min) {
		return { kind: 'error', code: 'below_min' };
	}
	const max = field.validationRules?.max;
	if (typeof max === 'number' && value > max) {
		return { kind: 'error', code: 'above_max' };
	}

	return { kind: 'value', value };
}

function optionValues(field: FormFieldDefinitionModel): Set<string> {
	return new Set((field.selectOptions ?? []).map((option) => option.value));
}

function validateSelect(
	field: FormFieldDefinitionModel,
	input: unknown,
): FormFieldOutcome {
	if (input == null || input === '') {
		return { kind: 'empty' };
	}

	const text = toText(input);
	if (text == null) {
		return { kind: 'error', code: 'invalid_type' };
	}

	if (!optionValues(field).has(text)) {
		return { kind: 'error', code: 'invalid_option' };
	}

	return { kind: 'value', value: text };
}

function validateMultiselect(
	field: FormFieldDefinitionModel,
	input: unknown,
): FormFieldOutcome {
	if (input == null || input === '') {
		return { kind: 'empty' };
	}

	const items: Array<unknown> = Array.isArray(input) ? input : [input];
	const allowed = optionValues(field);
	const chosen: Array<string> = [];

	for (const item of items) {
		const text = toText(item);
		if (text == null) {
			return { kind: 'error', code: 'invalid_type' };
		}
		if (!allowed.has(text)) {
			return { kind: 'error', code: 'invalid_option' };
		}
		if (!chosen.includes(text)) {
			chosen.push(text);
		}
	}

	if (chosen.length === 0) {
		return { kind: 'empty' };
	}

	return { kind: 'value', value: chosen };
}

/** True when year-month-day names a real day (no February 31st). */
function isCalendarDate(year: number, month: number, day: number): boolean {
	if (month < 1 || month > 12 || day < 1) {
		return false;
	}
	const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
	return day <= daysInMonth;
}

function validateDate(
	field: FormFieldDefinitionModel,
	input: unknown,
): FormFieldOutcome {
	if (input == null) {
		return { kind: 'empty' };
	}
	if (typeof input !== 'string') {
		return { kind: 'error', code: 'invalid_type' };
	}

	const value = input.trim();
	if (value.length === 0) {
		return { kind: 'empty' };
	}

	const match = DATE_PATTERN.exec(value);
	if (
		match == null ||
		!isCalendarDate(Number(match[1]), Number(match[2]), Number(match[3]))
	) {
		return { kind: 'error', code: 'invalid_date' };
	}

	// YYYY-MM-DD strings compare correctly as text.
	const min = field.validationRules?.min;
	if (typeof min === 'string' && value < min) {
		return { kind: 'error', code: 'below_min' };
	}
	const max = field.validationRules?.max;
	if (typeof max === 'string' && value > max) {
		return { kind: 'error', code: 'above_max' };
	}

	return { kind: 'value', value };
}

/**
 * A date and time as the page sent it. Kept as written rather than converted: a value
 * without a time zone (a browser `datetime-local` input) would otherwise be read in the
 * server's zone on one side and the visitor's on the other.
 */
function validateDatetime(
	field: FormFieldDefinitionModel,
	input: unknown,
): FormFieldOutcome {
	if (input == null) {
		return { kind: 'empty' };
	}
	if (typeof input !== 'string') {
		return { kind: 'error', code: 'invalid_type' };
	}

	const value = input.trim();
	if (value.length === 0) {
		return { kind: 'empty' };
	}
	if (value.length > maxLengthFor(field)) {
		return { kind: 'error', code: 'too_long' };
	}

	const match = DATETIME_PATTERN.exec(value);
	if (
		match == null ||
		!isCalendarDate(Number(match[1]), Number(match[2]), Number(match[3])) ||
		Number(match[4]) > 23 ||
		Number(match[5]) > 59
	) {
		return { kind: 'error', code: 'invalid_datetime' };
	}

	return { kind: 'value', value };
}
